import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	ActionBar,
	ActionCard,
	CloseIcon,
	Dialog,
	Input,
	Label,
	SpinnerWithBackground,
	Text,
	UnsavedChangesDialog,
	useUnsavedChangesWarning,
} from '@viapeople/components'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import UserHistoryModal from './components/UserHistoryModal'
import type { CreateUserInput, EditUserInput, User } from './types'

export function UserFormPage() {
	const navigate = useNavigate()
	const { userId, organizationId } = useParams<{ userId: string; organizationId: string }>()
	const isEditMode = !!userId
	const toast = useToast()
	const queryClient = useQueryClient()

	const backToUsersUrl = `/organizations/${organizationId}/users`

	const [isSubmitting, setIsSubmitting] = useState(false)
	const { data: populationLock } = useQuery({
		...trpc.admin.users.getPopulationLockStatus.queryOptions({ organizationId: organizationId as string }),
		enabled: !!organizationId,
	})
	const isPopulationLocked = populationLock?.locked === true
	const isPopulationLockPending = populationLock === undefined
	const isPopulationMutationBlocked = isPopulationLockPending || isPopulationLocked

	const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false)
	const [isUserHistoryModalOpen, setIsUserHistoryModalOpen] = useState(false)
	const handleArchiveConfirm = () => {
		handleArchiveUser()
		setIsArchiveModalOpen(false)
	}

	const { data: customFieldsData = [], isLoading: isCustomFieldsLoading } = useQuery({
		...trpc.admin.users.getCustomFields.queryOptions({ organizationId: organizationId as string }),
		enabled: !!organizationId,
	})
	const customFields = customFieldsData as string[]

	const {
		data: user,
		isLoading: isUserLoading,
		isError: isUserError,
	} = useQuery({
		...trpc.admin.users.getById.queryOptions({ id: userId as string, organizationId: organizationId as string }),
		enabled: isEditMode && !!userId && !!organizationId,
	})
	const userData = user as User | undefined

	const createUserMutation = useMutation({
		...trpc.admin.users.create.mutationOptions(),
		onSuccess: (data) => {
			// Reset form before navigation - map to form fields only
			reset({
				organizationId: data.organizationId,
				email: data.email,
				firstName: data.firstName,
				lastName: data.lastName,
				externalId: data.externalId,
				customFields: data.customFields,
			})
			toast.showToast('User created successfully')
			navigate(backToUsersUrl)
		},
		onError: (error) => {
			toast.showToast(`Error creating user: ${error.message}`, 'error')
			setIsSubmitting(false)
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.users.search.queryKey({ organizationId: organizationId as string }),
				exact: false,
				refetchType: 'all',
			})
			queryClient.invalidateQueries({
				queryKey: trpc.admin.userHistory.list.queryKey({ userId: userId as string }),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const editUserMutation = useMutation({
		...trpc.admin.users.edit.mutationOptions(),
		onSuccess: (data) => {
			// Reset form before navigation - map to form fields only
			reset({
				organizationId: data.organizationId,
				email: data.email,
				firstName: data.firstName,
				lastName: data.lastName,
				externalId: data.externalId,
				customFields: data.customFields,
			})
			toast.showToast('User updated successfully')
			navigate(backToUsersUrl)
		},
		onError: (error) => {
			toast.showToast(`Error updating user: ${error.message}`, 'error')
			setIsSubmitting(false)
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.users.search.queryKey({ organizationId: organizationId as string }),
				exact: false,
				refetchType: 'all',
			})
			queryClient.invalidateQueries({
				queryKey: trpc.admin.users.getById.queryKey({
					id: userId as string,
					organizationId: organizationId as string,
				}),
				exact: false,
				refetchType: 'all',
			})
			queryClient.invalidateQueries({
				queryKey: trpc.admin.userHistory.list.queryKey({ userId: userId as string }),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const archiveUserMutation = useMutation({
		...trpc.admin.users.delete.mutationOptions(),
		onSuccess: () => {
			reset() // Reset form before navigation
			toast.showToast('User archived')
			navigate(backToUsersUrl)
		},
		onError: (error) => {
			toast.showToast(`Error archiving user: ${error.message}`, 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.users.search.queryKey({ organizationId: organizationId as string }),
				exact: false,
				refetchType: 'all',
			})
			queryClient.invalidateQueries({
				queryKey: trpc.admin.users.getById.queryKey({
					id: userId as string,
					organizationId: organizationId as string,
				}),
				exact: false,
				refetchType: 'all',
			})
			queryClient.invalidateQueries({
				queryKey: trpc.admin.userHistory.list.queryKey({ userId: userId as string }),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const changeStatusMutation = useMutation({
		...trpc.admin.users.changeStatus.mutationOptions(),
		onSuccess: () => {
			toast.showToast(`User status changed to ${userData?.active ? 'inactive' : 'active'}`)
		},
		onError: (error) => {
			toast.showToast(`Error setting user to inactive: ${error.message}`, 'error')
		},
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.users.search.queryKey({ organizationId: organizationId as string }),
				exact: false,
				refetchType: 'all',
			})
			queryClient.invalidateQueries({
				queryKey: trpc.admin.users.getById.queryKey({
					id: userId as string,
					organizationId: organizationId as string,
				}),
				exact: false,
				refetchType: 'all',
			})
			queryClient.invalidateQueries({
				queryKey: trpc.admin.userHistory.list.queryKey({ userId: userId as string }),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const {
		handleSubmit,
		control,
		reset,
		formState: { errors, isDirty },
	} = useForm<Partial<EditUserInput>>({
		defaultValues: {
			email: '',
			firstName: '',
			lastName: '',
			externalId: '',
			customFields: {},
		},
	})

	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(isDirty)

	useEffect(() => {
		if (userData) {
			reset({
				organizationId: userData.organizationId,
				email: userData.email,
				firstName: userData.firstName,
				lastName: userData.lastName,
				externalId: userData.externalId,
				customFields: userData.customFields,
			})
		}
	}, [userData, reset])

	const handleFormSubmit = async (data: Partial<CreateUserInput | EditUserInput>) => {
		if (isPopulationMutationBlocked) {
			toast.showToast(
				isPopulationLockPending
					? 'Please wait while Data Feed lock status is being verified.'
					: (populationLock?.reason ?? 'Population editing is locked by Data Feed.'),
				'error',
			)
			return
		}

		setIsSubmitting(true)
		try {
			const { customFields, ...otherData } = data
			const formattedData = {
				...otherData,
				...(customFields || {}),
				organizationId: organizationId as string,
			}

			if (isEditMode && userId) {
				await editUserMutation.mutateAsync({
					...formattedData,
					id: userId,
				} as EditUserInput)
			} else {
				await createUserMutation.mutateAsync(formattedData as CreateUserInput)
			}
		} catch (error) {
			console.error('Failed to save user:', error)
		}
	}

	const handleChangeStatus = () => {
		if (isPopulationMutationBlocked) {
			toast.showToast(
				isPopulationLockPending
					? 'Please wait while Data Feed lock status is being verified.'
					: (populationLock?.reason ?? 'Population editing is locked by Data Feed.'),
				'error',
			)
			return
		}

		if (userId) {
			changeStatusMutation.mutate({
				userIds: [userId],
				organizationId: organizationId as string,
				active: !userData?.active,
			})
		}
	}

	const handleArchiveUser = () => {
		if (isPopulationMutationBlocked) {
			toast.showToast(
				isPopulationLockPending
					? 'Please wait while Data Feed lock status is being verified.'
					: (populationLock?.reason ?? 'Population editing is locked by Data Feed.'),
				'error',
			)
			return
		}

		if (userId) {
			archiveUserMutation.mutate({
				userIds: [userId],
				organizationId: organizationId as string,
			})
		}
	}

	const handleCancel = () => {
		navigate(backToUsersUrl)
	}

	const handleClose = () => {
		navigate(backToUsersUrl)
	}

	if (isUserLoading || isCustomFieldsLoading) {
		return <SpinnerWithBackground />
	}

	if (isUserError && isEditMode) {
		return (
			<main className='p-6'>
				<Text>Error loading user data. Please try again.</Text>
				<div className='mt-4'>
					<ActionBar
						rightActions={[
							{
								variant: 'secondary',
								label: 'Back to Users',
								onClick: handleCancel,
							},
						]}
					/>
				</div>
			</main>
		)
	}

	return (
		<main className='h-full flex flex-col relative'>
			<div className='flex justify-between items-center px-6 py-4 mb-6 border-b border-neutral-100'>
				{isEditMode ? (
					<div className='flex items-center gap-x-2'>
						<Text variant='medium' weight='semibold'>
							User profile
						</Text>
						<Label color={userData?.active ? 'green' : 'default'}>{userData?.active ? 'Active' : 'Inactive'}</Label>
						<Text variant='small'>VPID# {userData?.id}</Text>
					</div>
				) : (
					<Text variant='medium' weight='semibold'>
						Add user
					</Text>
				)}
				<button type='button' onClick={handleClose} className='p-1 hover:bg-neutral-50 rounded transition-colors'>
					<CloseIcon />
				</button>
			</div>
			<form onSubmit={handleSubmit(handleFormSubmit)} className='flex flex-col flex-grow w-full'>
				{isPopulationLocked && (
					<div className='mx-6 mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3'>
						<Text variant='small' className='text-amber-900'>
							{populationLock?.reason ??
								'Population editing is locked because this organization has a Data Feed configured.'}
						</Text>
					</div>
				)}
				<div className='grid grid-cols-1 md:grid-cols-3 gap-x6 xl:gap-x-12 gap-6 px-6 items-start'>
					<div>
						<Controller
							name='firstName'
							control={control}
							rules={{ required: 'First name is required' }}
							render={({ field }) => (
								<Input
									label='First Name'
									placeholder='First name'
									variant={errors.firstName ? 'error' : 'default'}
									hint={errors.firstName?.message as string | undefined}
									value={field.value || ''}
									onChange={field.onChange}
									onBlur={field.onBlur}
									disabled={isPopulationMutationBlocked}
								/>
							)}
						/>
					</div>

					<div>
						<Controller
							name='lastName'
							control={control}
							rules={{ required: 'Last name is required' }}
							render={({ field }) => (
								<Input
									label='Last Name'
									placeholder='Last name'
									variant={errors.lastName ? 'error' : 'default'}
									hint={errors.lastName?.message as string | undefined}
									value={field.value || ''}
									onChange={field.onChange}
									onBlur={field.onBlur}
									disabled={isPopulationMutationBlocked}
								/>
							)}
						/>
					</div>
					<div>
						<Controller
							name='externalId'
							control={control}
							rules={{ required: 'External ID is required' }}
							render={({ field }) => (
								<Input
									label='ID'
									placeholder='ID'
									variant={errors.externalId ? 'error' : 'default'}
									hint={errors.externalId?.message as string | undefined}
									value={field.value || ''}
									onChange={field.onChange}
									onBlur={field.onBlur}
									disabled={isPopulationMutationBlocked}
								/>
							)}
						/>
					</div>

					<div>
						<Controller
							name='email'
							control={control}
							rules={{
								required: 'Email is required',
								pattern: {
									value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
									message: 'Invalid email format',
								},
							}}
							render={({ field }) => (
								<Input
									label='Email'
									placeholder='Email address'
									variant={errors.email ? 'error' : 'default'}
									hint={errors.email?.message as string | undefined}
									value={field.value || ''}
									onChange={field.onChange}
									onBlur={field.onBlur}
									disabled={isPopulationMutationBlocked}
								/>
							)}
						/>
					</div>
					{customFields.map((fieldName) => (
						<div key={fieldName}>
							<Controller
								key={fieldName}
								name={`customFields.${fieldName}`}
								control={control}
								render={({ field }) => {
									let fieldValue = ''
									if (field.value !== undefined && field.value !== null) {
										fieldValue = typeof field.value === 'string' ? field.value : String(field.value)
									}

									return (
										<Input
											label={fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1')}
											placeholder={fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1')}
											variant={
												errors.customFields?.[fieldName as keyof typeof errors.customFields] ? 'error' : 'default'
											}
											hint={
												errors.customFields?.[fieldName as keyof typeof errors.customFields]
													? (
															errors.customFields[fieldName as keyof typeof errors.customFields] as unknown as {
																message?: string
															}
														)?.message
													: undefined
											}
											value={fieldValue}
											onChange={(e) => field.onChange(e)}
											onBlur={field.onBlur}
											disabled={isPopulationMutationBlocked}
										/>
									)
								}}
							/>
						</div>
					))}
				</div>

				<div className='flex-grow'></div>
				{isEditMode && !isPopulationMutationBlocked && (
					<div className='flex px-6 gap-x-6 mt-6 mb-27'>
						<ActionCard
							title={userData?.active ? 'Set to inactive' : 'Set to active'}
							variant='secondary'
							button={{
								children: userData?.active ? 'Set to inactive' : 'Set to active',
								size: 'small',
								onClick: handleChangeStatus,
							}}>
							Set this user's status to inactive.
						</ActionCard>
						<ActionCard
							title='Archive user'
							variant='destructive'
							button={{
								children: 'Archive user',
								size: 'small',
								onClick: () => setIsArchiveModalOpen(true),
							}}>
							Archive this user.{' '}
							<Text className='inline' variant='none' weight='semibold'>
								Note that this action cannot be reversed.
							</Text>
						</ActionCard>
					</div>
				)}

				<ActionBar
					leftActions={[
						{
							variant: 'secondary',
							label: 'User history',
							onClick: () => setIsUserHistoryModalOpen(true),
						},
					]}
					rightActions={[
						{
							variant: 'secondary',
							label: 'Cancel',
							onClick: handleCancel,
						},
						{
							variant: 'primary',
							label: isEditMode ? 'Save Changes' : 'Add user',
							onClick: handleSubmit(handleFormSubmit),
							disabled:
								isPopulationMutationBlocked ||
								isSubmitting ||
								createUserMutation.isPending ||
								editUserMutation.isPending,
						},
					]}
				/>
				{(isSubmitting ||
					createUserMutation.isPending ||
					editUserMutation.isPending ||
					changeStatusMutation.isPending) && <SpinnerWithBackground />}
			</form>
			<UserHistoryModal isOpen={isUserHistoryModalOpen} onClose={() => setIsUserHistoryModalOpen(false)} />
			<Dialog
				isOpen={isArchiveModalOpen}
				onClose={() => setIsArchiveModalOpen(false)}
				onConfirm={handleArchiveConfirm}
				title='Archive user'
				message='You are about to archive a user from this organization. This action cannot be reversed.'
				type='archive'
				confirmLabel='Archive'
				requireConfirmText
			/>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
		</main>
	)
}
